// Confirmación única de TODAS las mutaciones de Cobranza. Reasignar y redistribuir piden un motivo
// escrito porque es el `reason` que el lambda guarda en la auditoría de la asignación.
import { useId, useState } from 'react';
import { Button, Modal } from '@/kit';
import { userMessageOf } from '@/api/errors';

/** Qué se va a hacer y sobre quién; de aquí sale todo el copy del diálogo. */
export type MutationRequest =
    | { kind: 'reassign'; creditId: string; collectorLabel: string }
    | { kind: 'redistribute'; collectorLabel: string; activeAssignments: number }
    | { kind: 'activateRule'; strategyLabel: string; hasActiveRule: boolean }
    | {
          kind: 'saveRule';
          strategyLabel: string;
          /** La regla que se apaga al guardar esta como activa. */
          deactivatingLabel: string;
          isEdit: boolean;
      };

export interface MutationCopy {
    title: string;
    body: string;
    confirmLabel: string;
    /** El lambda exige un motivo para las mutaciones que mueven cartera. */
    requiresReason: boolean;
    /** Por qué no hay nada que confirmar; con esto el botón queda apagado. */
    blockedReason: string | null;
}

/** El copy exacto de cada mutación: qué se mueve, cuánto y de quién. */
export function describeMutation(request: MutationRequest): MutationCopy {
    switch (request.kind) {
        case 'reassign':
            return {
                title: 'Reasignar la asignación a otro cobrador',
                body: `Vas a mover el crédito ${request.creditId}, hoy asignado a ${request.collectorLabel}, a otro cobrador. El motor elige al nuevo con la regla de asignación activa.`,
                confirmLabel: 'Reasignar',
                requiresReason: true,
                blockedReason: null,
            };
        case 'redistribute': {
            const count = request.activeAssignments;
            // Sin cartera activa no hay nada que confirmar: prometer que no pasa nada y dejar el
            // botón vivo era decir dos cosas a la vez.
            if (count === 0) {
                return {
                    title: `Redistribuir la cartera de ${request.collectorLabel}`,
                    body: `${request.collectorLabel} no tiene asignaciones activas: no hay cartera que mover.`,
                    confirmLabel: 'Redistribuir',
                    requiresReason: true,
                    blockedReason: 'No hay cartera activa que redistribuir.',
                };
            }
            return {
                title: `Redistribuir la cartera de ${request.collectorLabel}`,
                body:
                    count === 1
                        ? `Vas a mover la asignación activa de ${request.collectorLabel} a otros cobradores.`
                        : `Vas a mover las ${String(count)} asignaciones activas de ${request.collectorLabel} a otros cobradores.`,
                confirmLabel: 'Redistribuir',
                requiresReason: true,
                blockedReason: null,
            };
        }
        case 'activateRule':
            return {
                title: `Activar la regla ${request.strategyLabel}`,
                body: request.hasActiveRule
                    ? `Desde ahora el motor asigna con ${request.strategyLabel}. Al activarla, la regla activa actual se desactiva.`
                    : `Desde ahora el motor asigna con ${request.strategyLabel}.`,
                confirmLabel: 'Activar',
                requiresReason: false,
                blockedReason: null,
            };
        case 'saveRule':
            return {
                title: request.isEdit
                    ? `Guardar y activar la regla ${request.strategyLabel}`
                    : `Crear y activar la regla ${request.strategyLabel}`,
                body: `Al guardarla activa, la regla ${request.deactivatingLabel} deja de aplicar. Desde ahora el motor asigna con ${request.strategyLabel}.`,
                confirmLabel: request.isEdit ? 'Guardar y activar' : 'Crear y activar',
                requiresReason: false,
                blockedReason: null,
            };
    }
}

export interface ConfirmMutationDialogProps {
    open: boolean;
    request: MutationRequest;
    isPending: boolean;
    /** El error que devolvió el lambda, si el intento anterior falló. */
    error: Error | null;
    onCancel: () => void;
    /** `reason` va vacío en las mutaciones que no lo piden. */
    onConfirm: (reason: string) => void;
}

export function ConfirmMutationDialog({
    open,
    request,
    isPending,
    error,
    onCancel,
    onConfirm,
}: ConfirmMutationDialogProps) {
    const fieldId = useId();
    const [reason, setReason] = useState('');
    const copy = describeMutation(request);
    const reasonMissing = copy.requiresReason && reason.trim() === '';
    const blocked = copy.blockedReason !== null;

    return (
        <Modal
            isOpen={open}
            onClose={onCancel}
            title={copy.title}
            description={copy.body}
            size="sm"
        >
            <div className="space-y-3">
                {copy.requiresReason ? (
                    <div className="space-y-1">
                        <label
                            className="text-[10px] font-bold uppercase tracking-[0.04em] text-muted-foreground"
                            htmlFor={`${fieldId}-reason`}
                        >
                            Motivo
                        </label>
                        <textarea
                            id={`${fieldId}-reason`}
                            rows={3}
                            value={reason}
                            placeholder="Ej. el cobrador salió de vacaciones"
                            onChange={(event) => {
                                setReason(event.target.value);
                            }}
                            className="w-full rounded border border-border bg-background px-2 py-1.5 text-[12px] outline-none focus-visible:border-ring"
                        />
                        <p className="text-[10.5px] text-muted-foreground">
                            Queda guardado en la auditoría de la asignación.
                        </p>
                    </div>
                ) : null}

                {error === null ? null : (
                    <p role="alert" className="text-[11.5px] text-destructive">
                        {userMessageOf(error)}
                    </p>
                )}

                <div className="flex items-center justify-end gap-2">
                    {copy.blockedReason === null ? null : (
                        <span className="mr-auto text-[11px] text-muted-foreground">
                            {copy.blockedReason}
                        </span>
                    )}
                    <Button variant="outline" onClick={onCancel} disabled={isPending}>
                        Cancelar
                    </Button>
                    <Button
                        disabled={reasonMissing || blocked}
                        isLoading={isPending}
                        onClick={() => {
                            onConfirm(reason.trim());
                        }}
                    >
                        {copy.confirmLabel}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
